import {getTestProps} from '@benchling/js-test-utils';
import {clamp} from '@benchling/js-utils';
import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {WorkspaceItem} from 'constants/FileBrowserConstants';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import {showEditNowButtonForSharedFile} from 'nav/share/growth/Util';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import isFocusSupercededByModal from 'ui-library/modals/isFocusSupercededByModal';
import {ElementMouseEvent} from 'util/Types';

import EditNowButton from './growth/EditNowButton';
import {isSwitchTabLeft, isSwitchTabRight} from './keyboard';
import {ClearWorkspaceActionType} from './WorkspaceConstants';
import WorkspaceItemTab from './WorkspaceItemTab';
import {OpenFileParams} from './WorkspaceItemTab';
import WorkspaceTabDropdown from './WorkspaceTabDropdown';

class WorkspaceConnector extends DataConnector<WorkspaceProps, ConnectedProps> {
  override stores = [WorkspaceStore, SidepanelStore];

  enhanceProps(): ConnectedProps {
    return {
      maxOverheadTabs: this.workspaceStore().getMaxOverheadTabs(),
    };
  }
}

export interface WorkspaceProps {
  openFile: (fileId: string, params?: OpenFileParams) => void;
  moveWorkspaceTab: (tabId: string, tabIndex: number) => void;
  onClearWorkspace: (itemIdsToKeep: Array<string>) => void;
  onUpdateWorkspaceWidth: (width: number) => void;
  activeId: string | null;
  workspaceItems: Array<WorkspaceItem>;
  currentUser: EntityStoreUser | null;
}

interface ConnectedProps {
  maxOverheadTabs: number;
}

interface State {
  // We need to maintain `intendedActiveId` so that the active tab state gets updated immediately
  // upon clicking on a tab, rather than having to wait for the newly opened file to load and update
  // the WorkspaceStore state.
  intendedActiveId: string | null;
  // Information that is constant for the lifetime of the drag operation, like the ID of the tab
  // being dragged. This is set on drag start and cleared on drag end, and nullness determines
  // whether or not a drag is happening.
  dragParams: {
    tabId: string;
    tabWidth: number;
    computeTranslateFromMouseX: (pageX: number) => number;
  } | null;
  // Information that changes as the item is being dragged, such as its position. This is
  // updated on every mousemove while dragging.
  dragState: {
    translateX: number;
    dragIndex: number;
  } | null;
}

export interface ClearWorkspaceContext {
  onClearWorkspace: (itemIdsToKeep: Array<string>) => void;
  workspaceItemIds: Array<string>;
}

export function handleClearWorkspaceAction(
  action: ClearWorkspaceActionType,
  context: ClearWorkspaceContext,
  itemId?: string
): void {
  const {workspaceItemIds, onClearWorkspace} = context;
  if (action === ClearWorkspaceActionType.CLOSE_SELF && itemId != null) {
    const itemIdsToKeep = workspaceItemIds.filter((fileId) => fileId !== itemId);
    onClearWorkspace(itemIdsToKeep);
  } else if (action === ClearWorkspaceActionType.CLOSE_OTHERS && itemId != null) {
    onClearWorkspace([itemId]);
  } else if (action === ClearWorkspaceActionType.CLOSE_RIGHT && itemId != null) {
    const itemIdsToKeep = workspaceItemIds.slice(0, workspaceItemIds.indexOf(itemId) + 1);
    onClearWorkspace(itemIdsToKeep);
  } else if (action === ClearWorkspaceActionType.CLOSE_ALL) {
    onClearWorkspace([]);
  }
}

enum TabDirection {
  LEFT = -1,
  RIGHT = 1,
}

export class Workspace extends React.Component<WorkspaceProps & ConnectedProps, State> {
  override state: State = {
    intendedActiveId: this.props.activeId,
    dragParams: null,
    dragState: null,
  };

  private _isMounted = false;
  private _throttledUpdateWidth: (() => void) | null = null;
  tabContainerRef = React.createRef<HTMLDivElement>();

  override componentDidMount(): void {
    this._isMounted = true;
    // Note that we intentionally use jQuery's resize event, since triggers on any event that might
    // cause this component to resize, not just when the window resizes.
    this._throttledUpdateWidth = benchlingUtil.resizeThrottler(this._updateWidth);
    $(window).on('resize', this._throttledUpdateWidth);
    $(window).on('buffersResize', this._updateWidth);
    // Initial call is to ensure width is set immediately in tests
    _.defer(() => {
      this._updateWidth();
    });
    document.addEventListener('keydown', this._handleKeyDown);
  }

  override UNSAFE_componentWillReceiveProps(nextProps: WorkspaceProps & ConnectedProps): void {
    if (this.props.activeId !== nextProps.activeId) {
      this.setState({intendedActiveId: nextProps.activeId});
    }
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
    $(window).off('resize', this._throttledUpdateWidth!);
    $(window).off('buffersResize', this._updateWidth);
    document.removeEventListener('keydown', this._handleKeyDown);

    // Ensure that we remove the event listeners prior to unmounting. Otherwise, if a test starts the drag and
    // it finishes after unmounting the component, it will raise a warning that a React state is being updated
    // on an unmounted component
    this._detachDragListeners();
  }

  _updateWidth = (): void => {
    // _throttledUpdateWidth calls _updateWidth asynchronously so guard in case it gets fired after unmount.
    if (!this._isMounted) {
      return;
    }
    this.props.onUpdateWorkspaceWidth(this._getWidth());
  };

  _handleDragStart = (e: ElementMouseEvent, index: number, tabWidth: number): void => {
    const workspaceItem = this.props.workspaceItems[index];
    this.props.openFile(workspaceItem.fileId);
    this.setState({intendedActiveId: workspaceItem.fileId});
    if (this.state.dragParams != null || this.props.workspaceItems.length <= 1) {
      return;
    }
    const initialTabTranslateX = index * tabWidth;
    const originalPageX = e.pageX;
    const maxX = this._getWidth() - tabWidth;
    const computeTranslateFromMouseX = (pageX: number): number => {
      // Given the screen coordinates of the mouse now, figure out the new x translation of the dragged tab.
      const deltaX = pageX - originalPageX;
      return clamp(0, maxX, initialTabTranslateX + deltaX);
    };

    this._attachDragListeners();
    this.setState({
      dragParams: {
        tabId: workspaceItem.fileId,
        tabWidth,
        computeTranslateFromMouseX,
      },
      dragState: {
        translateX: computeTranslateFromMouseX(e.pageX),
        dragIndex: index,
      },
    });
  };

  _getWidth(): number {
    return $(this.tabContainerRef.current!).width();
  }

  _handleDragMove = (e: MouseEvent): void => {
    if (this.state.dragParams == null) {
      return;
    }
    const {computeTranslateFromMouseX, tabId, tabWidth} = this.state.dragParams;
    const {dragIndex} = this.state.dragState!;
    const newTranslateX = computeTranslateFromMouseX(e.pageX);
    const newCenter = newTranslateX + tabWidth / 2;
    // We use the center of the dragged tab to determine its intended position. Note that we
    // currently assume that all tabs are the same width, so we can just use regular int division to
    // figure out the index of the desired position.
    const newDragIndex = Math.floor(newCenter / tabWidth);

    if (newDragIndex !== dragIndex) {
      this.props.moveWorkspaceTab(tabId, newDragIndex);
    }
    this.setState({
      dragState: {
        translateX: newTranslateX,
        dragIndex: newDragIndex,
      },
    });
  };

  _handleDragEnd = (): void => {
    this.setState({
      dragParams: null,
      dragState: null,
    });
    this._detachDragListeners();
  };

  _attachDragListeners(): void {
    // We need to listen to events on the whole window object since the mouse might move outside the
    // tab area for the drag operation and we're still interested in the mouse x value.
    window.addEventListener('mousemove', this._handleDragMove);
    window.addEventListener('mouseup', this._handleDragEnd);
  }

  _detachDragListeners(): void {
    window.removeEventListener('mousemove', this._handleDragMove);
    window.removeEventListener('mouseup', this._handleDragEnd);
  }

  _getModulo(n: number, d: number): number {
    return ((n % d) + d) % d;
  }

  _handleSwitchTab(direction: TabDirection): void {
    const tabIndex = this.props.workspaceItems.findIndex(
      (item) => item.fileId === this.state.intendedActiveId
    );
    const lastTabIndex = Math.min(this.props.workspaceItems.length, this.props.maxOverheadTabs);
    const newTab = this.props.workspaceItems[this._getModulo(tabIndex + direction, lastTabIndex)];

    this.props.openFile(newTab.fileId);
    this.setState({intendedActiveId: newTab.fileId});
  }

  _handleKeyDown = (e: KeyboardEvent): void => {
    if (
      isFocusSupercededByModal() ||
      SidepanelStore.getIsExpanded() ||
      FullScreenStore.getCurrentScreen() != null
    ) {
      return;
    }

    if (isSwitchTabLeft(e)) {
      this._handleSwitchTab(TabDirection.LEFT);
    }
    if (isSwitchTabRight(e)) {
      this._handleSwitchTab(TabDirection.RIGHT);
    }
  };

  getOverflowItems = (): Array<WorkspaceItem> => this.props.workspaceItems.slice(this.props.maxOverheadTabs);

  onClearWorkspace = (action: ClearWorkspaceActionType, itemId?: string): void => {
    const workspaceItemIds = this.props.workspaceItems.map(({fileId}) => fileId);
    const overflowItemIds = this.getOverflowItems().map(({fileId}) => fileId);
    const context = {workspaceItemIds, overflowItemIds, onClearWorkspace: this.props.onClearWorkspace};
    handleClearWorkspaceAction(action, context, itemId);
  };

  override render(): JSX.Element {
    const {openFile, maxOverheadTabs, workspaceItems} = this.props;
    const overheadItems = workspaceItems.slice(0, maxOverheadTabs);
    const overflowItems = this.getOverflowItems();
    const hasOverflow = overflowItems.length > 0;

    let draggedItem: WorkspaceItem | null = null;
    if (this.state.dragParams != null) {
      draggedItem =
        _.findWhere(workspaceItems, {
          fileId: this.state.dragParams.tabId,
        }) || null;
    }

    const overheadTabs = overheadItems.map((workspaceItem, index) => (
      <WorkspaceItemTab
        key={workspaceItem.id}
        item={workspaceItem}
        isActive={this.state.intendedActiveId === workspaceItem.fileId}
        isLastTab={index === workspaceItems.length - 1}
        isOverflowTab={false}
        forceMinWidth={hasOverflow}
        openFile={openFile}
        onClearWorkspace={this.onClearWorkspace}
        onDragStart={this._handleDragStart}
        index={index}
        isInvisible={draggedItem != null && draggedItem.id === workspaceItem.id}
      />
    ));

    if (draggedItem != null) {
      overheadTabs.unshift(
        <WorkspaceItemTab
          key="draggedItem"
          item={draggedItem}
          isActive={true}
          isLastTab={false}
          isOverflowTab={false}
          forceMinWidth={false}
          dragLayoutParams={{
            translateX: this.state.dragState!.translateX,
            width: this.state.dragParams!.tabWidth,
          }}
        />
      );
    }

    return (
      <div className="workspace" {...getTestProps({component: 'Workspace', element: 'root'})}>
        <div className="workspace-tabContainer js-navTour-workspace" ref={this.tabContainerRef}>
          {overheadTabs}
        </div>
        {hasOverflow && (
          <WorkspaceTabDropdown
            activeId={this.state.intendedActiveId}
            openFile={openFile}
            onClearWorkspace={this.onClearWorkspace}
            overflowItems={overflowItems}
          />
        )}
        {BenchlingConfig.ENABLE_SHOW_USERNAME_IN_WORKSPACE && this.props.currentUser && (
          <div className="workspace-usernameContainer">
            <a
              href={`/${this.props.currentUser.url_identifier}/settings`}
              className="workspace-usernameContainer-username"
            >
              <strong>{this.props.currentUser.name || this.props.currentUser.email}</strong>
            </a>
          </div>
        )}
        {showEditNowButtonForSharedFile() && <EditNowButton />}
      </div>
    );
  }
}

export default WorkspaceConnector.connect(Workspace, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});
